import { useState } from 'react'
import { isAxiosError } from 'axios'
import { Alert, Badge, Button, Group, Paper, Stack, Text, Title } from '@mantine/core'
import { useSearchParams } from 'react-router-dom'

import { usePedidosDeAnalise, usePegarPedido, type PedidoDeAnalise } from '@/api/robo'

/**
 * Pedidos de análise que o robô de um colega encaminhou (spec 2026-09-30 §4, P3): ele não
 * podia investigar produção pelo perfil do dono e pediu a quem pode. O primeiro que pegar
 * fica com o pedido. A lista vem do servidor já filtrada pelo que a pessoa pegaria agora.
 */
const MOTIVO: Record<number, string> = {
  409: 'Outra pessoa já pegou.',
  403: 'Você não tem mais permissão para este pedido.',
  404: 'Este pedido não existe mais.',
}

function Pedido({ p, destacado }: { p: PedidoDeAnalise; destacado: boolean }) {
  const pegar = usePegarPedido()
  const [erro, setErro] = useState<string | null>(null)
  const clicar = async () => {
    setErro(null)
    try {
      await pegar.mutateAsync(p.id)
    } catch (e) {
      const status = isAxiosError(e) ? e.response?.status ?? 0 : 0
      setErro(MOTIVO[status] ?? 'Não consegui pegar o pedido. Tente de novo.')
    }
  }
  return (
    <Paper withBorder p="md" data-destacado={destacado || undefined}
      style={destacado ? { borderColor: 'var(--mantine-primary-color-filled)' } : undefined}>
      <Stack gap={4}>
        <Group gap="xs">
          <Text fw={600}>{p.alvo}</Text>
          <Text size="sm" c="dimmed">{p.ambiente}</Text>
          {p.estado === 'pego' && <Badge variant="light">Com você</Badge>}
        </Group>
        <Text size="sm">{p.pedinte || 'Um colega'} perguntou: “{p.pergunta}”</Text>
        {p.estado === 'aberto' && (
          <Group gap="sm" mt={4}>
            <Button size="xs" loading={pegar.isPending} onClick={clicar}>Pegar</Button>
            {erro && <Text size="sm" c="red">{erro}</Text>}
          </Group>
        )}
      </Stack>
    </Paper>
  )
}

export function PedidosDeAnalise() {
  const { data, isError, isLoading } = usePedidosDeAnalise({ refetchInterval: 30000 })
  const [params] = useSearchParams()
  const alvo = Number(params.get('pedido') || 0)
  return (
    <Stack p="md" gap="md">
      <div>
        <Title order={2}>Pedidos de análise</Title>
        <Text size="sm" c="dimmed">
          Quando o perfil de alguém não investiga produção, o robô dessa pessoa pede a quem pode.
          O primeiro que pegar fica com o pedido.
        </Text>
      </div>
      {isError && <Alert color="red" variant="light">Não consegui ler os pedidos agora.</Alert>}
      {isLoading && <Text c="dimmed">Carregando…</Text>}
      {data && data.length === 0 && <Text size="sm">Nenhum pedido esperando por você.</Text>}
      {data?.map((p) => <Pedido key={p.id} p={p} destacado={p.id === alvo} />)}
    </Stack>
  )
}
